import { useEffect, useMemo, useRef, useState } from 'react';

import { addDays } from '@actual-app/core/shared/mtxbudget';
import type { Occurrence } from '@actual-app/core/shared/mtxbudget';
import {
  withArrangements,
  withProvided,
} from '@actual-app/core/shared/mtxbudget-completer';
import type {
  Check,
  WalkInputs,
  WalkStep,
} from '@actual-app/core/shared/mtxbudget-parcours';
import {
  expandSchedules,
  investmentCharges,
  planStart,
} from '@actual-app/core/shared/mtxbudget-schedules';
import { q } from '@actual-app/core/shared/query';

import { useSchedules } from '#hooks/useSchedules';

import { useByContent } from './byContent';
import { useRecalcul } from './recalcul';
import { solverCheck, solveTask } from './solver';
import { useMtxbudget } from './useMtxbudget';

/** Six months: thirteen pays. */
export const PARCOURS_PAYS = 13;

export type Parcours = {
  status: 'loading' | 'ready' | 'failed';
  steps: WalkStep[];
  /** The self-checks of this walk, and the reference case the solver must find. */
  checks: Check[];
  /** Which recalculation, asked by hand or not, this walk answers. */
  generation: number;
  /** Every scheduled charge of the horizon, switched off or not. */
  occurrences: Occurrence[];
  /** What the walk was computed from, to try a change before saving it. */
  inputs: WalkInputs | null;
};

/**
 * The walk through the next thirteen pays: what each one receives, pays, sets
 * aside and sends to the debts, with the checks that prove the arithmetic.
 */
export function useParcours(): Parcours {
  const { inputs, occurrences, isLoading } = useWalkInputs();
  const { generation } = useRecalcul();
  // Who asks the solver, for its self-check: many askers mean remounts.
  const origin = useRef(Math.random().toString(36).slice(2)).current;
  const [result, setResult] = useState<
    Omit<Parcours, 'occurrences' | 'inputs'>
  >({
    status: 'loading',
    steps: [],
    checks: [],
    generation: -1,
  });

  useEffect(() => {
    if (!inputs || isLoading) {
      return;
    }
    let cancelled = false;
    // In the solver's own Worker: the screen stays usable while it works.
    solveTask({ kind: 'walkChecked', inputs }, origin)
      .then(({ steps, checks }) => ({
        status: 'ready' as const,
        steps,
        checks: [...checks, solverCheck()],
      }))
      .catch((error: unknown) => ({
        status: 'failed' as const,
        steps: [],
        checks: [
          {
            name: 'Le solveur a répondu',
            ok: false,
            detail: error instanceof Error ? error.message : 'erreur inconnue',
          },
        ],
      }))
      .then(next => {
        if (!cancelled) {
          setResult({ ...next, generation });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [inputs, isLoading, generation, origin]);

  return { ...result, occurrences, inputs };
}

/**
 * What the walk is computed from: the pays, the charges of the horizon, the
 * cash on hand and the debts, rebuilt only when their content changes.
 */
export function useWalkInputs(): {
  inputs: WalkInputs | null;
  occurrences: Occurrence[];
  isLoading: boolean;
} {
  const { config, today, available, debts, isLoading } = useMtxbudget();
  const query = useMemo(() => q('schedules').select('*'), []);
  const { schedules, statuses } = useSchedules({ query });

  const paid = useMemo(
    () =>
      new Set(
        [...statuses].flatMap(([id, status]) =>
          status === 'paid' ? [id] : [],
        ),
      ),
    [statuses],
  );
  // The same start as Ma quinzaine: a pay not shown yet still counts.
  const from = config ? planStart(config.pay, schedules, today, paid) : today;

  const occurrences = useMemo(() => {
    const end = addDays(today, 14 * (PARCOURS_PAYS + 1));
    // An investment to pay before the debts is a charge like any other.
    // An amount the user gave stands in place of its estimate, and a bill
    // arranged with its creditor is paid in two, as on Ma quinzaine.
    return withArrangements(
      withProvided(
        expandSchedules(schedules, from, end, paid).concat(
          investmentCharges(config?.projects ?? [], from, end),
        ),
        config?.provided ?? {},
      ),
      config?.arrangements ?? {},
    ).sort((a, b) => a.date.localeCompare(b.date));
  }, [schedules, paid, from, today, config]);

  // The debts are rebuilt on every render: the walk is only recomputed when
  // what it is made from really changes.
  const fresh: WalkInputs | null = config
    ? {
        config: config.pay,
        today,
        from,
        occurrences,
        pays: config.pays,
        available,
        cushion: config.cushion,
        incomes: config.extraIncomes,
        skipped: config.skipped,
        debts,
        needs: config.projects.filter(project => !project.mandatory),
        count: PARCOURS_PAYS,
      }
    : null;
  const inputs = useByContent(fresh);

  return { inputs, occurrences, isLoading };
}
